// @flow

import React from 'react'
import { connect } from 'react-redux'
import bindAll from 'lodash/bindAll'
import { List, Map, fromJS } from 'immutable'
import { Field, reduxForm } from 'redux-form'
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table'
import moment from 'moment'
import Notifications from 'react-notification-system-redux'

import RenderInput from '../partials/RenderInput'
import RenderSelect from '../partials/RenderSelect'
import PaginationSupport from '../partials/PaginationSupport'
import TableLoader from '../partials/TableLoader'
import validate from './validate'

import {
	accountSetupsFetchData,
	accountSetupsAddFilterPageNumber,
	accountSetupsAddFilterColPerPage,
	accountSetupCreate,
	accountSetupResend
} from '../../actions/account_setup'

require('es6-promise').polyfill()

const ACCOUNT_TYPE_OPTIONS = fromJS([
	{ value: 'university', label: 'University' },
	{ value: 'company', label: 'Company' }
])

const COUNTRY_OPTIONS = fromJS([
	{ value: 'Indonesia', label: 'Indonesia' },
	{ value: 'Malaysia', label: 'Malaysia' },
	{ value: 'Singapore', label: 'Singapore' },
	{ value: 'Philippines', label: 'Philippines' },
	{ value: 'Thailand', label: 'Thailand' },
	{ value: 'Vietnam', label: 'Vietnam' },
	{ value: 'Australia', label: 'Australia' },
	{ value: 'United States', label: 'United States' },
	{ value: 'United Kingdom', label: 'United Kingdom' },
	{ value: 'India', label: 'India' },
	{ value: 'Japan', label: 'Japan' },
	{ value: 'South Korea', label: 'South Korea' },
	{ value: 'China', label: 'China' },
	{ value: 'Other', label: 'Other' }
])

type Props = {
	handleSubmit: Function,
	accountSetups: List<Map<string, any>>,
	accountSetupsPagination: Map<string, any>,
	accountSetupsFilterData: Map<string, any>,
	accountSetupsFetchData: Function,
	accountSetupsAddFilterPageNumber: Function,
	accountSetupsAddFilterColPerPage: Function,
	accountSetupCreate: Function,
	accountSetupResend: Function,
	globalIndicatorData: Map<string, any>,
	isFetched: boolean,
	submitting: boolean,
	pristine: boolean,
}

class AccountSetup extends React.Component<Props> {
	constructor(props) {
		super(props)

		bindAll(this, [
			'handleSaveData',
			'handleResend',
			'handleChangePageSize',
			'dateFormatter',
			'accountTypeFormatter',
			'actionFormatter'
		])
	}

	componentDidMount() {
		const { accountSetupsFetchData, accountSetupsFilterData } = this.props
		accountSetupsFetchData(accountSetupsFilterData)
	}

	handleSaveData(values) {
		const { accountSetupCreate, accountSetupsFetchData, accountSetupsFilterData } = this.props
		accountSetupCreate(values)
			.then(() => {
				accountSetupsFetchData(accountSetupsFilterData)
			})
			.catch(() => {})
	}

	handleResend(id) {
		const { accountSetupResend, accountSetupsFetchData, accountSetupsFilterData } = this.props
		accountSetupResend(id)
			.then(() => {
				accountSetupsFetchData(accountSetupsFilterData)
			})
	}

	handleChangePageSize(e) {
		const {
			accountSetupsAddFilterPageNumber,
			accountSetupsAddFilterColPerPage,
			accountSetupsFetchData
		} = this.props

		accountSetupsAddFilterPageNumber(1)
			.then(() => {
				accountSetupsAddFilterColPerPage(e.target.value)
					.then(() => {
						accountSetupsFetchData(this.props.accountSetupsFilterData)
					})
			})
	}

	dateFormatter(cell, row) {
		return <span>{row.created_at ? moment(row.created_at).format('MMM D, YYYY HH:mm') : '-'}</span>
	}

	accountTypeFormatter(cell, row) {
		const label = row.account_type === 'university' ? 'University' : 'Company'
		const cls = row.account_type === 'university' ? 'label label-info' : 'label label-primary'
		return <span className={cls}>{label}</span>
	}

	actionFormatter(cell, row) {
		return (
			<button
				className="btn-support-action primary p-l-12 p-r-12 p-t-8 p-b-8"
				onClick={() => this.handleResend(row.id)}
			>
				Resend
			</button>
		)
	}

	render() {
		const {
			handleSubmit,
			accountSetups,
			accountSetupsPagination,
			accountSetupsFilterData,
			accountSetupsFetchData,
			accountSetupsAddFilterPageNumber,
			globalIndicatorData,
			isFetched,
			submitting,
			pristine
		} = this.props

		const isIndicatorClass = globalIndicatorData.get('isShow') ? 'p-t-56' : ''

		let reportPagination = null
		if (!accountSetups.isEmpty()) {
			reportPagination = (
				<PaginationSupport
					pagination={accountSetupsPagination}
					fetchData={accountSetupsFetchData}
					filterData={accountSetupsFilterData}
					addPageNumber={accountSetupsAddFilterPageNumber}
				/>
			)
		}

		const options = {
			noDataText: <TableLoader isFetched={isFetched} />
		}

		return (
			<div className={isIndicatorClass}>
				<div className="div-support-container">
					<section className="wrapper">
						<div className="main-content">
							<div className="container-fluid">
								<div className="big-panel">

									<div className="flex-container">
										<h5 className="title-heading">Account Setup</h5>
									</div>

									<div className="panel panel-default m-t-24">
										<div className="panel-heading">
											<h4 className="panel-title">Create New Account</h4>
										</div>
										<div className="panel-body">
											<form onSubmit={handleSubmit(this.handleSaveData)}>
												<div className="row">
													<div className="col-md-6">
														<Field
															name="organization_name"
															type="text"
															component={RenderInput}
															label="Organization Name"
															placeholder="Enter organization name"
															className="form-control"
														/>
													</div>
													<div className="col-md-6">
														<Field
															name="account_type"
															component={RenderSelect}
															label="Account Type"
															dropdownOption={ACCOUNT_TYPE_OPTIONS}
															disableOptionText="Select account type"
														/>
													</div>
												</div>
												<div className="row">
													<div className="col-md-6">
														<Field
															name="admin_name"
															type="text"
															component={RenderInput}
															label="Admin Name"
															placeholder="Enter admin name"
															className="form-control"
														/>
													</div>
													<div className="col-md-6">
														<Field
															name="admin_email"
															type="email"
															component={RenderInput}
															label="Admin Email"
															placeholder="Enter admin email"
															className="form-control"
														/>
													</div>
												</div>
												<div className="row">
													<div className="col-md-6">
														<Field
															name="country"
															component={RenderSelect}
															label="Country"
															dropdownOption={COUNTRY_OPTIONS}
															disableOptionText="Select country"
														/>
													</div>
												</div>
												<div className="row m-t-16">
													<div className="col-md-12">
														<button
															type="submit"
															className="btn-support-action primary p-l-24 p-r-24 p-t-12 p-b-12"
															disabled={pristine || submitting}
														>
															{submitting ? 'Creating...' : 'Create Account & Send Invite'}
														</button>
													</div>
												</div>
											</form>
										</div>
									</div>

									<div className="flex-container flex-between m-t-32">
										<h5 className="title-heading">Setup History</h5>
										<div className="flex-container vertical-item--center">
											Show
											<select
												className="select-status-user"
												value={accountSetupsFilterData.get('per_page')}
												onChange={(e) => this.handleChangePageSize(e)}
											>
												<option value="10">10</option>
												<option value="20">20</option>
												<option value="30">30</option>
											</select>
											Entries
										</div>
									</div>

									<div className="report-list-container form-custom support-company-list m-t-16">
										<BootstrapTable
											data={accountSetups.toJS()}
											striped={true}
											options={options}
											hover
										>
											<TableHeaderColumn dataField="id" isKey={true} width="60">ID</TableHeaderColumn>
											<TableHeaderColumn dataField="organization_name" dataSort={true}>Organization Name</TableHeaderColumn>
											<TableHeaderColumn dataField="admin_name" dataSort={true}>Admin Name</TableHeaderColumn>
											<TableHeaderColumn dataField="admin_email" dataSort={true}>Admin Email</TableHeaderColumn>
											<TableHeaderColumn dataField="account_type" dataFormat={this.accountTypeFormatter} dataSort={true}>Account Type</TableHeaderColumn>
											<TableHeaderColumn dataField="country" dataSort={true}>Country</TableHeaderColumn>
											<TableHeaderColumn dataField="created_at" dataFormat={this.dateFormatter} dataSort={true}>Created At</TableHeaderColumn>
											<TableHeaderColumn dataField="email_status" dataSort={true}>Email Status</TableHeaderColumn>
											<TableHeaderColumn dataField="id" dataFormat={this.actionFormatter} width="100">Action</TableHeaderColumn>
										</BootstrapTable>
										{reportPagination}
									</div>

								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
		)
	}
}

AccountSetup = reduxForm({
	form: 'AccountSetupForm',
	validate,
	enableReinitialize: true
})(AccountSetup)

const mapStateToProps = (state) => ({
	accountSetups: state.accountSetups,
	accountSetupsPagination: state.accountSetupsPagination,
	accountSetupsFilterData: state.accountSetupsFilterData,
	globalIndicatorData: state.globalIndicatorData,
	isFetched: state.isFetched
})

const mapDispatchToProps = (dispatch) => ({
	accountSetupsFetchData: (filterData) => dispatch(accountSetupsFetchData(filterData)),
	accountSetupsAddFilterPageNumber: (page) => dispatch(accountSetupsAddFilterPageNumber(page)),
	accountSetupsAddFilterColPerPage: (per_page) => dispatch(accountSetupsAddFilterColPerPage(per_page)),
	accountSetupCreate: (data) => dispatch(accountSetupCreate(data)),
	accountSetupResend: (id) => dispatch(accountSetupResend(id))
})

export default connect(mapStateToProps, mapDispatchToProps)(AccountSetup)
